import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import axios from "axios";
import { showAlert } from "redux/actions/setAppActions";
import { Spinner } from "components/Spinner";
import { SureAlert } from "components/SureAlert";
import "./InvitedPsychologistsList.css";

export const InvitedPsychologistsList = ({ schoolId, isOwner }) => {
    const { t } = useTranslation("schools");
    const dispatch = useDispatch();
    const [psychologists, setPsychologists] = useState(null);
    const [loading, setLoading] = useState(true);
    const [removingId, setRemovingId] = useState(null);
    const [psychToRemove, setPsychToRemove] = useState(null);

    const loadPsychologists = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await axios.get(`schools/${schoolId}/psychologists`);
            setPsychologists(data);
        } catch (error) {
            console.error("Error loading psychologists:", error);
            dispatch(showAlert({ 
                type: "error", 
                text: error.response?.data?.message || t("load_psychologists_error") 
            }));
        } finally {
            setLoading(false);
        }
    }, [schoolId, dispatch, t]);

    useEffect(() => {
        if (schoolId) {
            loadPsychologists();
        }
    }, [schoolId, loadPsychologists]);

    const handleRemoveClick = (psychId) => {
        setPsychToRemove(psychId);
    };

    const handleRemoveConfirm = async () => {
        if (!psychToRemove) return;

        setRemovingId(psychToRemove);
        try {
            await axios.delete(`schools/${schoolId}/psychs/${psychToRemove}`);
            dispatch(showAlert({ type: "success", text: t("remove_psychologist_success") }));
            // Обновляем список
            await loadPsychologists();
        } catch (error) {
            const errorMessage = error.response?.data?.message || t("remove_psychologist_error");
            dispatch(showAlert({ type: "error", text: errorMessage }));
        } finally {
            setRemovingId(null);
            setPsychToRemove(null);
        }
    };

    const handleRemoveCancel = () => {
        setPsychToRemove(null);
    };

    if (loading) {
        return (
            <div style={{ padding: "20px 0", textAlign: "center" }}>
                <Spinner />
            </div>
        );
    }

    if (!psychologists) {
        return null;
    }

    return (
        <div className="invited-psychologists">
            <h4 style={{ marginBottom: 16 }}>{t("invited_psychologists_title")}</h4>

            {/* Владелец школы */}
            <div className="psychologist-item owner">
                <div className="psychologist-info">
                    <div className="psychologist-name">
                        {psychologists.owner.surname} {psychologists.owner.name}
                    </div>
                    <div className="psychologist-login">@{psychologists.owner.login}</div>
                    <div className="psychologist-role owner-badge">{t("school_owner")}</div>
                </div>
            </div>

            {/* Приглашенные психологи */}
            {psychologists.invited && psychologists.invited.length > 0 ? (
                <>
                    <h5 style={{ marginTop: 24, marginBottom: 12 }}>{t("invited_psychologists")}</h5>
                    {psychologists.invited.map((psych) => (
                        <div key={psych._id} className="psychologist-item invited">
                            <div className="psychologist-info">
                                <div className="psychologist-name">
                                    {psych.surname} {psych.name}
                                </div>
                                <div className="psychologist-login">@{psych.login}</div>
                            </div>
                            {isOwner && (
                                <button
                                    className="remove-btn"
                                    onClick={() => handleRemoveClick(psych._id)}
                                    disabled={removingId === psych._id}
                                    title={t("remove_psychologist")}
                                >
                                    {removingId === psych._id ? "..." : "×"}
                                </button>
                            )}
                        </div>
                    ))}
                </>
            ) : (
                <p className="no-invited-text">{t("no_invited_psychologists")}</p>
            )}

            {/* Модалка подтверждения удаления */}
            {psychToRemove && (
                <SureAlert
                    title={t("remove_psychologist_title")}
                    text={t("remove_psychologist_confirm")}
                    handleSubmit={handleRemoveConfirm}
                    handleClose={handleRemoveCancel}
                    submitText={t("remove_button")}
                />
            )}
        </div>
    );
};
